import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Speed as PerformanceIcon,
  Storage as CacheIcon,
  Warning as WarningIcon,
  Download as ExportIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { usePerformanceMetrics } from '../../hooks/useFallbackQuery';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`performance-tabpanel-${index}`}
      aria-labelledby={`performance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export interface PerformanceDashboardProps {
  timeWindow?: number;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({
  timeWindow = 60 * 60 * 1000, // 1 hour default
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds
}) => {
  const {
    metrics,
    getEfficiencyReport,
    getTopSlowQueries,
    exportMetrics,
    clearMetrics,
  } = usePerformanceMetrics(timeWindow);

  const [tabValue, setTabValue] = useState(0);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportData, setExportData] = useState('');

  const efficiencyReport = getEfficiencyReport();
  const slowQueries = getTopSlowQueries(10);

  const handleExport = () => {
    const data = exportMetrics();
    setExportData(data);
    setShowExportDialog(true);
  };

  const handleDownloadExport = () => {
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-metrics-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowExportDialog(false);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
    return `${Math.round(ms / 3600000)}h`;
  };

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Performance Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Export metrics">
            <IconButton onClick={handleExport}>
              <ExportIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear metrics">
            <IconButton onClick={clearMetrics}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Overall Efficiency Score */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PerformanceIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Overall Cache Efficiency</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={efficiencyReport.overallEfficiency}
                color={getScoreColor(efficiencyReport.overallEfficiency)}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
            <Typography variant="h4" color={`${getScoreColor(efficiencyReport.overallEfficiency)}.main`}>
              {efficiencyReport.overallEfficiency}%
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Critical Issues and Recommendations */}
      {(efficiencyReport.criticalIssues.length > 0 || efficiencyReport.recommendations.length > 0) && (
        <Box sx={{ mb: 3 }}>
          {efficiencyReport.criticalIssues.map((issue, index) => (
            <Alert key={index} severity="error" sx={{ mb: 1 }}>
              {issue}
            </Alert>
          ))}
          {efficiencyReport.recommendations.map((recommendation, index) => (
            <Alert key={index} severity="info" sx={{ mb: 1 }}>
              {recommendation}
            </Alert>
          ))}
        </Box>
      )}

      {/* Metrics Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CacheIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Cache Hit Rate</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {formatPercentage(metrics.cacheHitRate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {metrics.cacheHits} hits / {metrics.totalRequests} requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Avg Response Time</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {formatDuration(metrics.averageResponseTime)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Across all requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WarningIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">API Failure Rate</Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {formatPercentage(metrics.apiFailureRate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {metrics.apiFailures} failures
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingDownIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Fallback Usage</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {formatPercentage(metrics.fallbackUsageRate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {metrics.fallbackUsages} fallback uses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Slow Queries" />
            <Tab label="Detailed Metrics" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Top 10 Slowest Queries
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Query Key</TableCell>
                  <TableCell align="right">Avg Response Time</TableCell>
                  <TableCell align="right">Request Count</TableCell>
                  <TableCell align="center">Performance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {slowQueries.map((query, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {query.queryKey}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatDuration(query.averageResponseTime)}
                    </TableCell>
                    <TableCell align="right">{query.requestCount}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={query.averageResponseTime > 2000 ? 'Slow' : query.averageResponseTime > 1000 ? 'Medium' : 'Fast'}
                        color={query.averageResponseTime > 2000 ? 'error' : query.averageResponseTime > 1000 ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Detailed Performance Metrics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Request Statistics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Total Requests:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {metrics.totalRequests}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Cache Hits:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {metrics.cacheHits}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Cache Misses:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {metrics.cacheMisses}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">API Failures:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {metrics.apiFailures}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Fallback Uses:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {metrics.fallbackUsages}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Data Quality
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Average Data Age:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatDuration(metrics.dataFreshness)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Cache Hit Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPercentage(metrics.cacheHitRate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">API Failure Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPercentage(metrics.apiFailureRate)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Fallback Usage Rate:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPercentage(metrics.fallbackUsageRate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onClose={() => setShowExportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Export Performance Metrics</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Performance metrics data ready for export:
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 400, overflow: 'auto' }}>
            <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {exportData}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExportDialog(false)}>Cancel</Button>
          <Button onClick={handleDownloadExport} variant="contained">
            Download JSON
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};